import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// `success` / `warning` / `destructive` / `info` use the bold `.es-*` palette
// from globals.css (solid teal / amber / brick-red / sea-foam on cream text)
// in light theme — matching the employee profile hero card. The previous
// `bg-emerald-500/10 text-emerald-600` etc. produced near-invisible pills on
// the cream/white background. `.dark .es-*` overrides restore the soft
// translucent look for dark theme, so this is a pure light-mode contrast fix.
// `whitespace-nowrap` keeps a pill a pill: a label wider than its container used
// to wrap inside the badge, which broke the rounded shape and inflated the row it
// sat in. Callers whose label can be arbitrarily long pair this with a max width
// and a truncating span (see the skill chips on the candidates list).
const badgeVariants = cva(
  'inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium transition-colors',
  {
    variants: {
      variant: {
        default: 'bg-muted text-muted-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        destructive: 'es-terminated',
        outline: 'border border-border text-foreground',
        success: 'es-active',
        warning: 'es-probation',
        info: 'es-onboarding',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };

